import { lazy, Suspense, useEffect, useRef } from 'react';
import { useDocument } from '@/firebase/hooks/useDocument';
import { useDispatch, useSelector } from '@/store';
import { userSelector, userTypeSelector } from '@/store/selectors/auth';
import { logout, setUserInfo } from '@/store/slices/auth';
import { User as IUser } from '@/types/User';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import Navbar from './components/Navbar/Navbar';
import Login from '@/screens/Login/Login';
import { doc, updateDoc } from 'firebase/firestore';
import firestore from '@/firebase/firestore';
import { onIdTokenChanged } from 'firebase/auth';
import auth from '@/firebase/auth';
import {
  canImpersonate,
  clearImpersonatedUid,
  getImpersonatedUid,
  isImpersonatingUser
} from '@/helpers/impersonation';
import AQLConfigList from '@/screens/AQLConfig/AQLConfigList';
import AQLConfigDetails from '@/screens/AQLConfig/AQLConfigDetails';

const SettingsLazy = lazy(() => import('@/screens/Settings/Settings'));
const PoListLazy = lazy(() => import('@/screens/PO/PoList'));
const PoFormLazy = lazy(() => import('@/screens/PO/PoForm'));
const DashboardLazy = lazy(() => import('@/screens/Dashboard/Dashboard'));
const ProfileLazy = lazy(() => import('@/screens/Profile/Profile'));
const UsersLazy = lazy(() => import('@/screens/Settings/Users'));
const UserLazy = lazy(() => import('@/screens/Settings/User'));
const ReportDetailsLazy = lazy(() => import('@/screens/CheckistTemplates/ReportDetails'));
const ReportsLazy = lazy(() => import('@/screens/CheckistTemplates/Reports'));
const FactoriesLazy = lazy(() => import('@/screens/Factories/Factories'));
const ClientsLazy = lazy(() => import('@/screens/Clients/Clients'));
const OnboardingLazy = lazy(() => import('@/screens/Onboarding/Onboarding'));
const ForgotPasswordLazy = lazy(() => import('@/screens/ForgotPassword/ForgotPassword'));
const NotFoundLazy = lazy(() => import('@/screens/NotFound/NotFound'));
const PoInspectionLazy = lazy(() => import('@/screens/PoInspection/PoInspection'));
const PoReportLazy = lazy(() => import('@/screens/PoReport/PoReport'));
const PoAdminFormLazy = lazy(() => import('@/screens/PO/PoAdminForm'));

const LoggedInRouter = () => {
  const userType = useSelector(userTypeSelector);

  return (
    <>
      <Navbar />
      <div className="container mx-auto">
        <div className="py-4 sm:px-2">
          <Routes>
            <Route path="/" element={<Navigate to="/po" />} />
            <Route path="/po" element={<PoListLazy />} />
            <Route path="/po/:id" element={<PoFormLazy />} />
            <Route path="/po/:id/inspection" element={<PoInspectionLazy />} />
            <Route path="/po/:id/report" element={<PoReportLazy />} />

            {userType === 'admin' && <Route path="/admin/po/:id" element={<PoAdminFormLazy />} />}
            {userType === 'admin' && (
              <Route path="/settings" element={<SettingsLazy />}>
                <Route path="users" element={<UsersLazy />} />
                <Route path="checklist-templates" element={<ReportsLazy />} />
                <Route path="checklist-templates/:id" element={<ReportDetailsLazy />} />
                <Route path="aql-configurations" element={<AQLConfigList />} />
                <Route path="aql-configurations/:id" element={<AQLConfigDetails />} />
                <Route path="factories" element={<FactoriesLazy />} />
                <Route path="clients" element={<ClientsLazy />} />
                <Route path="users/:id" element={<UserLazy />} />
              </Route>
            )}
            {userType === 'admin' && <Route path="dashboard" element={<DashboardLazy />} />}
            <Route path="profile" element={<ProfileLazy />} />
            <Route path="onboarding/:email" element={<OnboardingLazy />} />
            <Route path="*" element={<NotFoundLazy />} />
          </Routes>
        </div>
      </div>
    </>
  );
};

const LoggedOutRouter = () => (
  <Routes>
    <Route path="/" element={<Login />} />
    <Route path="/forgot-password" element={<ForgotPasswordLazy />} />
    <Route path="onboarding/:email" element={<OnboardingLazy />} />
    <Route path="*" element={<NotFoundLazy />} />
  </Routes>
);

function Navigation() {
  const user = useSelector(userSelector);
  const canImpersonateUsers = canImpersonate();
  const impersonatedUid = getImpersonatedUid();
  const effectiveUid = canImpersonateUsers && impersonatedUid ? impersonatedUid : user?.uid;
  const isImpersonating = isImpersonatingUser(user?.uid);
  const { data } = useDocument('users', effectiveUid, false);
  const firstTime = useRef(true);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!canImpersonateUsers && impersonatedUid) {
      clearImpersonatedUid();
    }
  }, [canImpersonateUsers, impersonatedUid]);

  // Force logout when Firebase detects the session has expired or been revoked.
  // This catches the case where Redux persist still has a user but the Firebase
  // auth token is no longer valid (surfaced as Firestore CORS/channel errors).
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(auth, (firebaseUser) => {
      if (!firebaseUser && user) {
        dispatch(logout());
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data) {
      if (data.deletedAt) {
        dispatch(logout());
        return;
      }
      dispatch(setUserInfo(data as unknown as IUser));
      if (
        firstTime.current &&
        !isImpersonating &&
        data.uid &&
        // Update last login if it's been more than 2 hours
        (!data.lastLogin ||
          new Date(data.lastLogin.toDate()).getTime() < new Date().getTime() - 1000 * 60 * 60 * 2)
      ) {
        firstTime.current = false;
        updateDoc(doc(firestore, 'users', data.uid), {
          lastLogin: new Date()
        });
      }
    }
  }, [data, dispatch, isImpersonating]);
  return (
    <BrowserRouter>
      <Suspense fallback={<div></div>}>{user ? <LoggedInRouter /> : <LoggedOutRouter />}</Suspense>
    </BrowserRouter>
  );
}

export default Navigation;
